import PropTypes from 'prop-types'

import classNames from 'classnames'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import {
  LEGEND_COLUMNS,
  LEGEND_ROWS,
  buildLegendData
} from '@aytm/app/stats/page/question-views/cq/components/partial/lib/legend'

import style from '@aytm/app/stats/page/question-views/cq/components/partial/styles/Legend.module.scss'

// Ported from the Backbone question template; all styling is local to Legend.module.scss.
const CELL_WIDTH = 92
const COLUMN_WIDTH = 98

const SigTestLegend = ({ totals, data, notes, percentDecimals }) => (
  <div className={style.section}>
    <div className={style.sectionTitle}>
      Sigtest
      <br />
      <span className={style.small}>
        <b>Example:</b> statistical significance test - z-test based
      </span>
    </div>
    <div className={style.row}>
      <div className={style.detailed}>
        <div className={style.dataView}>
          <div className={style.grid} style={{ width: CELL_WIDTH + COLUMN_WIDTH * LEGEND_COLUMNS.length }}>
            <div className={style.gridRow}>
              <div className={style.cellHeader} style={{ width: CELL_WIDTH }}>
                &nbsp;
              </div>
              {LEGEND_COLUMNS.map((column, i) => (
                <div key={column[0]} className={style.cellHeader} style={{ width: COLUMN_WIDTH }}>
                  <span className={style.cellTitle}>
                    {column[0]} (n={totals[i]})
                  </span>
                  <div className={style.sigtestLetter}>{column[1] || ''}</div>
                </div>
              ))}
            </div>
            {LEGEND_ROWS.map((row, i) => (
              <div key={row} className={style.gridRow}>
                <div className={style.cellAnswer} style={{ left: 0, width: CELL_WIDTH }}>
                  <b className={style.position}>A{i + 1}:</b> {row}
                </div>
                {data[i].map((cell, j) => (
                  <div key={LEGEND_COLUMNS[j][0]} className={style.cell} style={{ width: COLUMN_WIDTH }}>
                    <span className={style.text}>
                      {cell[0]}
                      <br />
                      {formatAmounts(cell[1], null, percentDecimals)}%
                    </span>
                    {0 < cell[2].length && (
                      <span className={style.sigtest}>
                        <span className={style.sigtestValue}>{cell[2]}</span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={style.description}>
        {LEGEND_ROWS.map((row, i) => (
          <div key={row}>
            {0 < (notes[i] || []).length ? (
              notes[i].map(note => <p key={note} dangerouslySetInnerHTML={{ __html: note }} />)
            ) : (
              <>&nbsp;</>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
)

SigTestLegend.propTypes = {
  totals: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  notes: PropTypes.array.isRequired,
  percentDecimals: PropTypes.number
}

const CrosstabLegend = () => (
  <div className={style.section}>
    <div className={style.sectionTitle}>
      Correlation Scan <span className={style.small}>(Chi-Square based)</span>
    </div>
    <div className={classNames(style.row, style.crosstabsRow)}>
      <span className={classNames(style.crosstabsCell, style.upTop)}>
        <i className="icon-aytmfd-up" />
        <i className="icon-aytmfd-up" />
        <i className="icon-aytmfd-up" />
      </span>
      <span className={classNames(style.crosstabsCell, style.upMiddle)}>
        <i className="icon-aytmfd-up" />
        <i className="icon-aytmfd-up" />
      </span>
      <span className={classNames(style.crosstabsCell, style.upLow)}>
        <i className="icon-aytmfd-up" />
      </span>
      <span className={style.crosstabsText}>
        Different levels of
        <br />
        positive deviation
      </span>
      <span className={classNames(style.crosstabsCell, style.rowGrey)} />
      <span className={style.crosstabsText}>
        Insignificant
        <br />
        deviation
      </span>
    </div>
    <div className={classNames(style.row, style.crosstabsRow)}>
      <span className={classNames(style.crosstabsCell, style.downTop)}>
        <i className="icon-aytmfd-down" />
        <i className="icon-aytmfd-down" />
        <i className="icon-aytmfd-down" />
      </span>
      <span className={classNames(style.crosstabsCell, style.downMiddle)}>
        <i className="icon-aytmfd-down" />
        <i className="icon-aytmfd-down" />
      </span>
      <span className={classNames(style.crosstabsCell, style.downLow)}>
        <i className="icon-aytmfd-down" />
      </span>
      <span className={style.crosstabsText}>
        Different levels of
        <br />
        negative deviation
      </span>
      <span className={classNames(style.crosstabsCell, style.rowWhite)} />
      <span className={style.crosstabsText}>
        Not enough
        <br />
        data
      </span>
    </div>
  </div>
)

const Legend = ({
  mode,
  confidenceLevel,
  significanceLevel,
  percentDecimals,
  isReportXpert,
  onViewOptionsClick,
  onClose
}) => {
  const isSigTest = 'sigtest' === mode
  const legendData = isSigTest ? buildLegendData({ confidenceLevel, significanceLevel }) : null

  // The confidence pair drives which reference table is shown; an unknown pair has no table.
  if (isSigTest && !legendData) return null

  const noteOffset = CELL_WIDTH + COLUMN_WIDTH * LEGEND_COLUMNS.length

  return (
    <div className={style.component} data-testid="stat-dl-cq-legend">
      <div className={style.content}>
        <div className={style.title}>Legend</div>
        {isSigTest && (
          <div
            className={style.note}
            data-testid="stat-dl-cq-legend-note"
            style={{ left: noteOffset + 40, width: `calc(100% - ${noteOffset + 70}px)` }}
          >
            <span className={style.noteIcon}>
              <i className="icon-aytmfd-crosstab-v2" />
            </span>
            {isReportXpert ? (
              <b>Confidence level is set to:</b>
            ) : (
              <>
                <b>
                  You can adjust Confidence level in{' '}
                  <span
                    data-testid="stat-dl-cq-legend-view-options"
                    className={style.viewOptionsLink}
                    {...pressable(event => {
                      event.preventDefault()
                      onViewOptionsClick?.()
                    })}
                  >
                    View Options tab
                  </span>
                </b>
                . <b>Currently set to:</b>
              </>
            )}
            <br />
            Primary confidence level = <b>{100 * confidenceLevel}%</b>
            <br />
            Secondary confidence level = <b>{100 * significanceLevel}%</b>
          </div>
        )}
        <span
          data-testid="stat-dl-cq-legend-close"
          className={style.close}
          aria-label="Close legend"
          {...pressable(onClose)}
        >
          <i className="icon-aytmfd-x-v2" />
        </span>
        {isSigTest && (
          <SigTestLegend
            totals={legendData.totals}
            data={legendData.data}
            notes={legendData.notes}
            percentDecimals={percentDecimals}
          />
        )}
        {'crosstab' === mode && <CrosstabLegend />}
      </div>
    </div>
  )
}

Legend.propTypes = {
  mode: PropTypes.oneOf(['sigtest', 'crosstab']),
  confidenceLevel: PropTypes.number,
  significanceLevel: PropTypes.number,
  percentDecimals: PropTypes.number,
  isReportXpert: PropTypes.bool,
  onViewOptionsClick: PropTypes.func,
  onClose: PropTypes.func
}

Legend.displayName = 'CorrelationFinderLegend'

export default Legend
